import React from 'react'
import { Loader } from 'react-feather'
import { CaretRight, Lock } from '@phosphor-icons/react'
import { Button, Icon } from '@shopflo/ui'
import { useLocale } from 'src/lib/hooks/useLocal'
import AppLogo from './AppLogo'
import DocumentationLinks, { DocumentationLink } from './DocumentationLinks'
import StatusBadge from './StatusBadge'

interface IntegrationDetailSidebarProps {
  name: string
  logoUrl?: string
  categoryLabel?: string | null
  isActive: boolean
  externallyManaged: boolean
  activating: boolean
  activationError: string | null
  ctaLabel: string
  isConfigureCta?: boolean
  onCtaClick: () => void
  showUpdateCta: boolean
  updating: boolean
  onUpdateClick: () => void
  documentations?: DocumentationLink[]
  helpUrl?: string
  requiresAdditionalApp?: boolean
  onAdditionalAppClick?: () => void
}

const IntegrationDetailSidebar: React.FC<IntegrationDetailSidebarProps> = ({
  name,
  logoUrl,
  categoryLabel,
  isActive,
  externallyManaged,
  activating,
  activationError,
  ctaLabel,
  isConfigureCta = false,
  onCtaClick,
  showUpdateCta,
  updating,
  onUpdateClick,
  documentations,
  helpUrl,
  requiresAdditionalApp = false,
  onAdditionalAppClick
}) => {
  const { t } = useLocale()
  const hasDocs = (documentations?.filter(d => d.url && d.name).length ?? 0) > 0 || Boolean(helpUrl?.trim())

  return (
    <>
      <div className='flex mb-2'>
        <AppLogo src={logoUrl} name={name} size='xl' />
      </div>

      <div className='flex flex-col mb-6'>
        <h2 className='text-text-lg-semibold text-zinc-900'>{name}</h2>
        <div className='flex flex-col gap-2'>
          {categoryLabel && <span className='text-text-xs-medium text-zinc-500'>{categoryLabel}</span>}
          {!requiresAdditionalApp && <StatusBadge variant={isActive ? 'active' : 'inactive'} />}
        </div>
      </div>

      {activationError && <p className='text-text-xs-regular text-red-600 -mb-4'>{activationError}</p>}

      {requiresAdditionalApp ? (
        <div className='flex flex-col gap-1'>
          <div className='flex h-7 w-7 items-center justify-center rounded-lg border border-zinc-200 bg-white'>
            <Icon IconComponent={Lock} size='sm' iconProps={{ weight: 'fill' }} className='text-yellow-600' />
          </div>
          <div className='flex flex-col gap-1'>
            <p className='text-text-xs-semibold text-zinc-700'>Integration requires an additional app</p>
            <p className='text-text-xs-medium text-zinc-500'>
              This integration requires the Smart Estimated Delivery app. Install it to show customers their estimated
              delivery date at checkout.
            </p>
          </div>
          <div
            role='button'
            tabIndex={0}
            onClick={onAdditionalAppClick}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') onAdditionalAppClick?.()
            }}
            className='flex h-8 w-full cursor-pointer items-center gap-1 p-0 text-left text-text-xs-semibold text-zinc-700 transition-colors hover:text-zinc-900 pt-1'
          >
            <img src='https://image.shopflo.com/smart_etd.svg' alt='' className='h-6 w-6 flex-shrink-0' />
            Enable Smart Estimated Delivery
            <Icon IconComponent={CaretRight} size='3xs' iconProps={{ weight: 'bold' }} className='text-zinc-500' />
          </div>
        </div>
      ) : (
        <div className='flex flex-col gap-1.5'>
          {showUpdateCta && (
            <Button variant='primary' size='md' className='w-full' onClick={onUpdateClick} disabled={updating}>
              {updating ? (
                <span className='flex items-center justify-center gap-1.5'>
                  <Loader size={14} className='animate-spin' />
                  {t('updating')}
                </span>
              ) : (
                t('update_configuration')
              )}
            </Button>
          )}
          <Button
            variant={!isActive && !showUpdateCta && !isConfigureCta ? 'primary' : 'outline'}
            size='md'
            className='w-full'
            onClick={onCtaClick}
            disabled={activating}
          >
            {activating ? (
              <span className='flex items-center justify-center gap-1.5'>
                <Loader size={14} className='animate-spin' />
                {isActive ? 'Deactivating…' : 'Activating…'}
              </span>
            ) : (
              ctaLabel
            )}
          </Button>
        </div>
      )}

      {hasDocs && <div className='border-t border-zinc-200 my-5' />}
      <DocumentationLinks documentations={documentations} helpUrl={helpUrl} />
    </>
  )
}

export default IntegrationDetailSidebar
